import clsx from 'clsx'
import Loader from 'components/Loader/Loader'
import TablePagination from 'components/TablePagination/TablePagination'
import { StationApi } from 'api/StationApi'
import StationItem from 'pages/Assets/stations/StationItem/StationItem'
import { LotLiveTelemetry, parseLotTelemetryRow } from 'pages/Assets/stations/stationLotOccupancy'
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PROGRESS_TYPES, TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { ILot, IStation } from 'utils/interfaces'
import { sortWithFavoritesFirst } from 'utils/favoritesSort'
import { useUser } from 'hooks/user'

import stationStyle from 'pages/Assets/stations/AssetsStationsTable.module.css'

/** Station edit from table context menu. */
const STATION_EDIT_ENABLED = true
/** Backend `telemetry-snapshots` accepts at most 100 ids per request. */
const TELEMETRY_SNAPSHOT_BATCH_SIZE = 100

export type AssetsStationsPanelProps = {
  variant: 'page' | 'panel'
  stations: IStation[]
  stationsForStats: IStation[]
  lots: ILot[]
  loadingProgress?: PROGRESS_TYPES
  highlightedStationId?: number
  onStationsUpdated: () => void
  onStationsChanged: () => void
  /** Push live online/feedback/diagnostics into parent list so Assets sort/pagination update immediately. */
  onLiveStationFields?: (fieldsById: Record<number, Partial<IStation>>) => void
  currentPage?: number
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>
  totalCount?: number
  pageSize?: number
  onPageSizeChange?: (size: number) => void
  pageInput?: string
  setPageInput?: (val: string) => void
  handlePageJump?: (e: KeyboardEvent<HTMLInputElement>) => void
  panelPage?: number
  panelPageSize?: number
  panelPageInput?: string
  setPanelPage?: React.Dispatch<React.SetStateAction<number>>
  setPanelPageInput?: (val: string) => void
  onPanelPageJump?: (e: KeyboardEvent<HTMLInputElement>) => void
  isActive?: boolean
  emptyTitle?: string
  emptyDescription?: string
}

const AssetsStationsPanel = ({
  variant,
  stations,
  stationsForStats,
  lots,
  loadingProgress = PROGRESS_TYPES.SUCCESS,
  highlightedStationId,
  onStationsUpdated,
  onStationsChanged,
  onLiveStationFields,
  currentPage = 1,
  setCurrentPage,
  totalCount = 0,
  pageSize = 20,
  onPageSizeChange,
  pageInput = '1',
  setPageInput,
  handlePageJump,
  panelPage = 1,
  panelPageSize = 20,
  panelPageInput = '1',
  setPanelPage,
  setPanelPageInput,
  onPanelPageJump,
  isActive = true,
  emptyTitle,
  emptyDescription,
}: AssetsStationsPanelProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const tableRef = useRef<HTMLDivElement>(null)
  const pageSizeRef = useRef(pageSize)
  pageSizeRef.current = pageSize

  useEffect(() => {
    if (variant !== 'page' || !onPageSizeChange || !isActive) {
      return
    }
    const tableElement = tableRef.current
    if (!tableElement) {
      return
    }

    const getFallbackRowHeight = () => {
      const styles = getComputedStyle(document.documentElement)
      const rowHeightVar = styles.getPropertyValue('--row-height').trim()
      const parsedRowHeight = Number.parseFloat(rowHeightVar)
      if (!Number.isNaN(parsedRowHeight) && parsedRowHeight > 0) {
        return parsedRowHeight
      }
      return 52
    }

    const recalculatePageSize = () => {
      const firstRow = tableElement.firstElementChild as HTMLElement | null
      const rowHeight = firstRow?.getBoundingClientRect().height || getFallbackRowHeight()
      if (rowHeight <= 0) {
        return
      }
      const maxVisibleRows = Math.max(1, Math.floor(tableElement.clientHeight / rowHeight))
      if (maxVisibleRows !== pageSizeRef.current) {
        onPageSizeChange(maxVisibleRows)
      }
    }

    recalculatePageSize()
    const observer = new ResizeObserver(recalculatePageSize)
    observer.observe(tableElement)
    window.addEventListener('resize', recalculatePageSize)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', recalculatePageSize)
    }
  }, [variant, onPageSizeChange, isActive, stationsForStats.length])

  const favoriteStationIds = useMemo(
    () => new Set(user?.state.favoriteStationIds ?? []),
    [user?.state.favoriteStationIds]
  )
  const orderedStations = useMemo(
    () => (variant === 'panel' ? sortWithFavoritesFirst(stations, favoriteStationIds) : stations),
    [variant, stations, favoriteStationIds]
  )
  const visibleStations = useMemo(() => {
    if (variant === 'panel') {
      return orderedStations.slice((panelPage - 1) * panelPageSize, panelPage * panelPageSize)
    }
    return orderedStations
  }, [variant, orderedStations, panelPage, panelPageSize])

  /** Stable id set for live poll — full list so diagnostics can re-sort across pages. */
  const pollStationIdsKey = useMemo(
    () =>
      [...stationsForStats.map(station => station.id)]
        .sort((a, b) => a - b)
        .join(','),
    [stationsForStats]
  )
  const [liveTelemetryByStationId, setLiveTelemetryByStationId] = useState<
    Record<number, Record<number, LotLiveTelemetry>>
  >({})
  /** Online / inventory / feedback / diagnostics from the same poll as Status occupancy. */
  const [liveStationFieldsById, setLiveStationFieldsById] = useState<Record<number, Partial<IStation>>>({})

  useEffect(() => {
    if (!isActive || !pollStationIdsKey) {
      return
    }

    const pollStationIds = pollStationIdsKey.split(',').map(Number)
    let cancelled = false

    const loadLiveTelemetry = async () => {
      try {
        const batches: Awaited<ReturnType<typeof StationApi.fetchTelemetrySnapshots>> = []
        for (let i = 0; i < pollStationIds.length; i += TELEMETRY_SNAPSHOT_BATCH_SIZE) {
          const chunk = pollStationIds.slice(i, i + TELEMETRY_SNAPSHOT_BATCH_SIZE)
          batches.push(...(await StationApi.fetchTelemetrySnapshots(chunk)))
        }
        const batch = batches
        const telemetryEntries = batch.map(item => {
          const byLotId: Record<number, LotLiveTelemetry> = {}
          for (const lot of item.snapshot?.lots ?? []) {
            byLotId[lot.lot.id] = parseLotTelemetryRow(lot)
          }
          return [item.id, byLotId] as const
        })
        const stationFieldEntries = batch.map(item => {
          const err = item.snapshot?.error
          const errorCode = Number(err?.error_code ?? 0)
          const fields: Partial<IStation> = {
            online: Boolean(item.online),
            lastTelemetryAt: item.last_telemetry_at ?? null,
            diagnostics_error_code: Number.isFinite(errorCode) ? errorCode : 0,
            diagnostics_error_msg: String(err?.error_msg || '').trim() || null,
          }
          if (item.feedback_status !== undefined) {
            fields.feedback_status = item.feedback_status
          }
          if (item.expects_telemetry !== undefined) {
            fields.expects_telemetry = item.expects_telemetry
          }
          if (item.inventory_mismatch !== undefined) {
            fields.inventory_mismatch = Boolean(item.inventory_mismatch)
          }
          if (item.lot_ids_only_in_db !== undefined) {
            fields.lot_ids_only_in_db = item.lot_ids_only_in_db
          }
          if (item.lot_ids_only_in_telemetry !== undefined) {
            fields.lot_ids_only_in_telemetry = item.lot_ids_only_in_telemetry
          }
          if (item.reported_lot_ids !== undefined) {
            fields.reported_lot_ids = item.reported_lot_ids
          }
          if (item.lot_model_mismatches !== undefined) {
            fields.lot_model_mismatches = item.lot_model_mismatches
          }
          return [item.id, fields] as const
        })
        if (!cancelled) {
          const fieldsById = Object.fromEntries(stationFieldEntries)
          setLiveTelemetryByStationId(Object.fromEntries(telemetryEntries))
          setLiveStationFieldsById(fieldsById)
          onLiveStationFields?.(fieldsById)
        }
      } catch (error) {
        console.warn('[AssetsStationsPanel] fetchTelemetrySnapshots failed', error)
        if (!cancelled) {
          setLiveTelemetryByStationId({})
          setLiveStationFieldsById({})
        }
      }
    }

    loadLiveTelemetry()
    const timer = window.setInterval(loadLiveTelemetry, TIMEOUT_FETCHING_STATUS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [isActive, pollStationIdsKey, onLiveStationFields])

  if (loadingProgress === PROGRESS_TYPES.WORK) {
    return <Loader />
  }

  if (loadingProgress === PROGRESS_TYPES.ERROR) {
    return (
      <div className={stationStyle.emptyWrapper}>
        <div className={stationStyle.emptyTitle}>{t('common.error')}</div>
      </div>
    )
  }

  return (
    <div className={stationStyle.tableWrapper}>
      <div className={stationStyle.tableScrollArea}>
        <div className={stationStyle.tableScrollContent}>
          <div className={stationStyle.tableHeader}>
            <div />
            <div className={clsx(stationStyle.tableHeaderItem, 'text-16-400')}>{t('robots.serial_id')}</div>
            <div className={clsx(stationStyle.tableHeaderItem, 'text-16-400')}>{t('robots.name')}</div>
            <div className={clsx(stationStyle.tableHeaderItem, 'text-16-400')}>Lots</div>
            <div className={clsx(stationStyle.tableHeaderItem, 'text-16-400')}>Status</div>
            <div />
          </div>
          {stationsForStats.length ? (
            <div className={stationStyle.table} ref={variant === 'page' ? tableRef : undefined}>
              {visibleStations.map(station => {
                const liveFields = liveStationFieldsById[station.id]
                const displayStation = liveFields ? { ...station, ...liveFields } : station
                return (
                  <StationItem
                    key={station.id}
                    station={displayStation}
                    stations={stationsForStats}
                    lots={lots}
                    liveLotTelemetryByLotId={liveTelemetryByStationId[station.id]}
                    onStationUpdated={onStationsUpdated}
                    onStationsChanged={onStationsChanged}
                    hightlighted={station.id === highlightedStationId}
                    hideDelete={variant === 'panel'}
                    disableEdit={!STATION_EDIT_ENABLED}
                  />
                )
              })}
            </div>
          ) : (
            <div className={stationStyle.emptyWrapper}>
              <div className={stationStyle.emptyTitle}>{emptyTitle || t('assets.no_stations_title')}</div>
              <div className={stationStyle.emptyDescription}>
                {emptyDescription || t('assets.no_stations_description')}
              </div>
            </div>
          )}
        </div>
      </div>
      {variant === 'page' && totalCount > pageSize && setCurrentPage && setPageInput && handlePageJump && (
        <div className={stationStyle.paginationFooter}>
          <TablePagination
            page={currentPage}
            totalPages={Math.ceil(totalCount / pageSize) || 1}
            pageInput={pageInput}
            onPageInputChange={setPageInput}
            onPageInputKeyDown={handlePageJump}
            onPageInputBlur={() => setPageInput(currentPage.toString())}
            onPrev={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            onNext={() => setCurrentPage(prev => prev + 1)}
          />
        </div>
      )}
      {variant === 'panel' && orderedStations.length > panelPageSize && setPanelPage && setPanelPageInput && (
        <div className={stationStyle.paginationFooter}>
          <TablePagination
            page={panelPage}
            totalPages={Math.ceil(orderedStations.length / panelPageSize) || 1}
            pageInput={panelPageInput}
            onPageInputChange={setPanelPageInput}
            onPageInputKeyDown={onPanelPageJump ?? (() => {})}
            onPageInputBlur={() => setPanelPageInput(panelPage.toString())}
            onPrev={() => setPanelPage(prev => Math.max(prev - 1, 1))}
            onNext={() => setPanelPage(prev => prev + 1)}
          />
        </div>
      )}
    </div>
  )
}

export default AssetsStationsPanel
